import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  MenuItem,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
} from '@mui/material';
import { Assessment, ShowChart, Label, Category, Visibility } from '@mui/icons-material';
import Plot from 'react-plotly.js';
import { useParams } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useProject } from '../contexts/ProjectContext';

const Reports = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const { labels, fetchLabels, sensors, fetchSensors } = useProject();

  const [filterMode, setFilterMode] = useState('status'); // 'status' or 'label'
  const [plotType, setPlotType] = useState('pca');
  const [status, setStatus] = useState('Labeled');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [selectedChoice, setSelectedChoice] = useState('');
  const [selectedSensor, setSelectedSensor] = useState('');
  const [plotData, setPlotData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Prediction samples
  const [predictionCandidates, setPredictionCandidates] = useState([]);
  const [selectedPredictions, setSelectedPredictions] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchLabels(projectId);
      fetchSensors(projectId).then((sensorsData) => {
        if (sensorsData && sensorsData.length > 0) {
          setSelectedSensor(sensorsData[0].id);
        }
      });
    }
  }, [projectId]);

  const fetchAllPages = async (endpoint) => {
    let allData = [];
    let nextUrl = endpoint;
    let page = 1;
    while (nextUrl) {
      const response = await api.get(nextUrl, { params: page === 1 ? {} : {} });
      const data = response.data.results || response.data || [];
      if (Array.isArray(data)) allData.push(...data);
      nextUrl = response.data.next;
      if (nextUrl && nextUrl.startsWith('http')) {
        const url = new URL(nextUrl);
        nextUrl = url.pathname + url.search;
      }
      page++;
    }
    return allData;
  };

  const fetchPredictionCandidates = useCallback(async () => {
    if (!projectId) {
      setPredictionCandidates([]);
      return;
    }

    // Show prediction panel for: label mode with a label selected, or status mode with Labeled/Cortex
    const showForStatus = filterMode === 'status' && (status === 'Labeled' || status === 'Cortex');
    const showForLabel = filterMode === 'label' && selectedLabel;

    if (!showForStatus && !showForLabel) {
      setPredictionCandidates([]);
      return;
    }

    setLoadingCandidates(true);
    try {
      // Always fetch parked samples as prediction candidates
      const parkedSamples = await fetchAllPages(`/project/${projectId}/samples/Parked/`);

      let candidates = parkedSamples.map(s => ({
        id: s.id || s.sample_id,
        name: s.name || s.sample_name,
        source: 'Parked',
      }));

      // In label mode, also get labeled samples not tagged with this label
      if (showForLabel) {
        try {
          const [taggedSamples, labeledSamples] = await Promise.all([
            fetchAllPages(`/project/${projectId}/samples-tags/label/${selectedLabel}/`),
            fetchAllPages(`/project/${projectId}/samples/Labeled/`),
          ]);
          const taggedIds = new Set(taggedSamples.map(s => s.sample_id || s.id));
          const parkedIds = new Set(candidates.map(c => c.id));
          const unlabeledForTag = labeledSamples
            .filter(s => {
              const id = s.id || s.sample_id;
              return !taggedIds.has(id) && !parkedIds.has(id);
            })
            .map(s => ({
              id: s.id || s.sample_id,
              name: s.name || s.sample_name,
              source: 'Unlabeled',
            }));
          candidates = [...candidates, ...unlabeledForTag];
        } catch {
          // ignore
        }
      }

      setPredictionCandidates(candidates);
      setSelectedPredictions([]);
    } catch (err) {
      console.error('Failed to fetch prediction candidates:', err);
      setPredictionCandidates([]);
    } finally {
      setLoadingCandidates(false);
    }
  }, [projectId, selectedLabel, filterMode, status]);

  useEffect(() => {
    fetchPredictionCandidates();
  }, [fetchPredictionCandidates]);

  const plotTypes = [
    { value: 'pca', label: 'PCA Plot', endpoint: 'pca' },
    { value: 'umap', label: 'UMAP Plot', endpoint: 'umap' },
    { value: 'qt', label: 'Q-T Plot', endpoint: 'qt' },
    { value: 'mahalanobis', label: 'Mahalanobis Distance', endpoint: 'mahalanobis' },
  ];

  const statuses = [
    { value: 'Labeled', label: 'Labeled' },
    { value: 'Cortex', label: 'Cortex' },
    { value: 'Parked', label: 'Parked' },
    { value: 'Legacy', label: 'Legacy' },
  ];

  const getSelectedLabelObject = () => {
    return labels.find(l => l.id === selectedLabel);
  };

  const togglePrediction = (sampleId) => {
    setSelectedPredictions(prev =>
      prev.includes(sampleId)
        ? prev.filter(id => id !== sampleId)
        : [...prev, sampleId]
    );
  };

  const selectAllPredictions = () => {
    if (selectedPredictions.length === predictionCandidates.length) {
      setSelectedPredictions([]);
    } else {
      setSelectedPredictions(predictionCandidates.map(c => c.id));
    }
  };

  const generatePlot = async () => {
    if (!projectId) {
      toast.error('No project selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const plotConfig = plotTypes.find((t) => t.value === plotType);
      let endpoint;
      let requestBody;

      if (filterMode === 'status') {
        // Filter by status
        endpoint = `/plots/project/${projectId}/${plotConfig.endpoint}/${status}/`;
        requestBody = {
          samples_to_remove: [],
          samples_to_predict: selectedPredictions,
        };
      } else {
        // Filter by label
        if (!selectedLabel) {
          toast.error('Please select a label');
          setLoading(false);
          return;
        }

        if (!selectedSensor) {
          toast.error('Please select a sensor');
          setLoading(false);
          return;
        }

        endpoint = `/plots/${plotConfig.endpoint}/label/${selectedLabel}/`;
        requestBody = {
          sensor_id: selectedSensor,
          samples_to_remove: [],
          samples_to_predict: selectedPredictions,
        };

        // If categorical label with choice selected, add choice_id
        const labelObj = getSelectedLabelObject();
        if (labelObj && labelObj.label_type === 'Categorical' && selectedChoice) {
          requestBody.choice_id = selectedChoice;
        }
      }

      const response = await api.post(endpoint, requestBody);

      // Parse the JSON string returned from backend
      const plotJson = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      setPlotData(plotJson);
      toast.success('Plot generated successfully');
    } catch (error) {
      console.error('Error generating plot:', error);
      const errorMsg = error.response?.data?.error || 'Failed to generate plot. Make sure you have enough samples.';
      setError(errorMsg);
      toast.error(errorMsg);
      setPlotData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterModeChange = (event, newMode) => {
    if (newMode !== null) {
      setFilterMode(newMode);
      setPlotData(null);
      setError(null);
    }
  };

  const handleLabelChange = (e) => {
    setSelectedLabel(e.target.value);
    setSelectedChoice('');
    setSelectedPredictions([]);
    setPlotData(null);
  };

  const handleFilterModeChangeWrapped = (event, newMode) => {
    handleFilterModeChange(event, newMode);
    setSelectedPredictions([]);
    setPredictionCandidates([]);
  };

  return (
    <MainLayout>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Reports & Visualization
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Exploratory data analysis and visualization tools
          </Typography>
        </Box>

        {/* Controls */}
        <Card sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Assessment sx={{ fontSize: 32, color: theme.palette.primary.main }} />
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  Generate Visualization
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select a plot type and filter to visualize your data
                </Typography>
              </Box>
            </Box>

            {/* Filter Mode Toggle */}
            <Box sx={{ mb: 3 }}>
              <ToggleButtonGroup
                value={filterMode}
                exclusive
                onChange={handleFilterModeChangeWrapped}
                aria-label="filter mode"
                size="small"
              >
                <ToggleButton value="status" aria-label="filter by status">
                  <Category sx={{ mr: 1 }} />
                  By Status
                </ToggleButton>
                <ToggleButton value="label" aria-label="filter by label">
                  <Label sx={{ mr: 1 }} />
                  By Label
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Plot Type"
                  value={plotType}
                  onChange={(e) => setPlotType(e.target.value)}
                >
                  {plotTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {filterMode === 'status' ? (
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Sample Status"
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); setSelectedPredictions([]); }}
                  >
                    {statuses.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      fullWidth
                      label="Label"
                      value={selectedLabel}
                      onChange={handleLabelChange}
                    >
                      <MenuItem value="">
                        <em>Select a label</em>
                      </MenuItem>
                      {labels.map((label) => (
                        <MenuItem key={label.id} value={label.id}>
                          {label.name} ({label.label_type})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Sensor selector for label-based plots */}
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      fullWidth
                      label="Sensor"
                      value={selectedSensor}
                      onChange={(e) => setSelectedSensor(e.target.value)}
                    >
                      {sensors.length === 0 ? (
                        <MenuItem value="">
                          <em>No sensors available</em>
                        </MenuItem>
                      ) : (
                        sensors.map((sensor) => (
                          <MenuItem key={sensor.id} value={sensor.id}>
                            {sensor.name}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                  </Grid>

                  {/* Show choice selector for categorical labels */}
                  {selectedLabel && getSelectedLabelObject()?.label_type === 'Categorical' && (
                    <Grid item xs={12} md={3}>
                      <TextField
                        select
                        fullWidth
                        label="Choice (Optional)"
                        value={selectedChoice}
                        onChange={(e) => setSelectedChoice(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>All choices</em>
                        </MenuItem>
                        {getSelectedLabelObject()?.choices?.map((choice) => (
                          <MenuItem key={choice.id} value={choice.id}>
                            {choice.choice || choice.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                </>
              )}

              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ShowChart />}
                  onClick={generatePlot}
                  disabled={loading}
                  sx={{ py: 1.8 }}
                >
                  {loading ? 'Generating...' : 'Generate Plot'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Prediction Samples */}
        {((filterMode === 'label' && selectedLabel) || (filterMode === 'status' && (status === 'Labeled' || status === 'Cortex'))) && (
          <Card sx={{ mb: 4, borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Visibility sx={{ color: theme.palette.secondary.main }} />
                  <Typography variant="h6" fontWeight={600}>
                    Prediction Samples
                  </Typography>
                  {selectedPredictions.length > 0 && (
                    <Chip
                      label={`${selectedPredictions.length} selected`}
                      size="small"
                      color="secondary"
                    />
                  )}
                </Box>
                {predictionCandidates.length > 0 && (
                  <Button size="small" onClick={selectAllPredictions}>
                    {selectedPredictions.length === predictionCandidates.length ? 'Deselect All' : 'Select All'}
                  </Button>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select parked or unlabeled samples to overlay as predictions (shown in red)
              </Typography>
              {loadingCandidates ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : predictionCandidates.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No prediction candidates available
                </Typography>
              ) : (
                <Paper sx={{ maxHeight: 200, overflow: 'auto', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                  <List dense>
                    {predictionCandidates.map((candidate) => (
                      <ListItem
                        key={candidate.id}
                        button
                        onClick={() => togglePrediction(candidate.id)}
                        sx={{
                          py: 0.25,
                          bgcolor: selectedPredictions.includes(candidate.id)
                            ? alpha(theme.palette.secondary.main, 0.1)
                            : 'transparent',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <Checkbox
                            edge="start"
                            checked={selectedPredictions.includes(candidate.id)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={candidate.name}
                          sx={{ '& .MuiListItemText-primary': { fontSize: '0.85rem' } }}
                        />
                        <Chip
                          label={candidate.source}
                          size="small"
                          variant="outlined"
                          color={candidate.source === 'Parked' ? 'warning' : 'default'}
                          sx={{ ml: 1 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </CardContent>
          </Card>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Visualization */}
        {plotData && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {plotTypes.find((t) => t.value === plotType)?.label || 'Visualization'}
                {filterMode === 'status' ? ` - ${status} Samples` : ` - ${getSelectedLabelObject()?.name || 'Label'}`}
              </Typography>
              <Box
                sx={{
                  mt: 3,
                  p: 3,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Plot
                  data={plotData.data}
                  layout={{
                    ...plotData.layout,
                    autosize: true,
                    width: undefined,
                    height: 600,
                    plot_bgcolor: 'rgba(0,0,0,0)',
                    paper_bgcolor: 'rgba(0,0,0,0)',
                  }}
                  useResizeHandler
                  config={{
                    responsive: true,
                    displayModeBar: true,
                    displaylogo: false,
                  }}
                  style={{ width: '100%', height: 600 }}
                />
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Initial State */}
        {!plotData && !loading && !error && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <Assessment sx={{ fontSize: 80, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Visualization Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select a plot type and filter, then click "Generate Plot" to visualize your data.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Container>
    </MainLayout>
  );
};

export default Reports;
